<template><slot></slot></template>
<script lang="ts" setup>
import { createPoint } from "@p-v/utils/factory"
import { inject, onUnmounted, provide, ref, watch } from "vue"
import MapMarkerPng from "@p-v/asset/image/map-mark-1.png"
import { v4 as uuid } from "uuid"
import type { Point, Size } from "@p-v/packages/Map/map"

const domId = `info-box-${uuid({ length: 8 })}`

interface ComponentProps {
  point: Point
  title: string
  image?: string
  opacity?: number
  offset?: Size
  size?: Size
  gpsType?: "GCJ02" | "BD09"
  data?: any
}

const props = withDefaults(defineProps<ComponentProps>(), {
  point: () => ({ lng: 116.40342230333138, lat: 39.92498414216742 }),
  title: () => "",
  image: () => "",
  opacity: () => 0.5,
  offset: () => ({ width: 0, height: 0 }),
  size: () => ({ width: 50, height: 60 }),
  gpsType: () => "BD09",
  data: () => ({}),
})

const bmap = inject("bmap") as any

function GCJ02toBD09(lng: number, lat: number) {
  // 3.14159265358979324
  let x_pi = (3.141592653589793 * 3000.0) / 180.0
  let x = lng
  let y = lat
  let z = Math.sqrt(x * x + y * y) + 0.00002 * Math.sin(y * x_pi)
  let theta = Math.atan2(y, x) + 0.000003 * Math.cos(x * x_pi)
  let lngs = z * Math.cos(theta) + 0.0064
  let lats = z * Math.sin(theta) + 0.0064
  return {
    lng: lngs,
    lat: lats,
  }
}

interface BOXRef {
  exposed: {
    boxEl: HTMLElement | null
    setData: (data: any, keyMap?: any) => void
    show: () => void
    hide: () => void
  }
}

const infoBoxRef = ref<BOXRef | undefined>(undefined)

const install = (instance: any) => {
  infoBoxRef.value = instance
}

provide("install", install)
provide("infoBoxId", domId)

const createDOM = () => {
  const div = document.createElement("div")
  div.id = domId
  div.style.cssText = `
    position: absolute;
    width: ${props.size.width}px;
    height: ${props.size.height}px;
    background-image: url(${props.image ? props.image : MapMarkerPng});
    background-size: 100%;
    background-repeat: no-repeat;
    background-position: top;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    font-size: 14px;
    color: #fff;
    text-align: center;
    position: relative;
  `

  const title = document.createElement("span")
  title.style.display = "block"
  title.style.lineHeight = "16px"
  title.style.fontSize = "12px"
  title.style.fontWeight = "700"
  title.style.width = "50px"
  // 内容超出宽度自动省略
  title.style.overflow = "hidden"
  title.style.textOverflow = "ellipsis"
  title.style.whiteSpace = "nowrap"
  title.style.padding = "0 2px"
  title.innerHTML = props.title
  div.appendChild(title)

  div.onclick = () => {
    infoBoxRef.value?.exposed.show()
  }
  return div
}

const overlay = ref<any>(undefined)

const updateOverlay = () => {
  if (overlay.value) {
    bmap.value?.removeOverlay(overlay.value)
  }
  const newPoint = props.gpsType === "GCJ02" ? GCJ02toBD09(props.point.lng, props.point.lat) : props.point
  overlay.value = new window.BMapGL.CustomOverlay(createDOM, {
    point: createPoint(window.BMapGL, newPoint),
    opacity: props.opacity,
    offsetY: props.offset.height,
    offsetX: props.offset.width,
  })
  bmap.value?.addOverlay(overlay.value)
}
watch(
  () => bmap.value,
  value => {
    if (value) {
      updateOverlay()
    }
  },
  { immediate: true },
)

onUnmounted(() => {
  bmap.value?.removeOverlay(overlay.value)
})
</script>
